import { useMemo, useRef, useState, useCallback } from "react";
import { DataGridPremium, useGridApiRef } from "@mui/x-data-grid-premium";
import { TextField } from "@mui/material";
import MultiSelectCheckEditCell from "../editors/MultiSelectCheckEditCell";
import { Database, Map } from "lucide-react";
import ActionsRenderer from "../editors/renderers/ActionsRenderer";
import NexusQueryEditor from "./NexusQueryEditor";

/**
 * NexusDataGrid (MUI Premium)
 * - Mirrors NexusGrid's public API but uses MUI DataGridPremium under the hood
 * - Supports the same header actions (Add, SQL, Mapping)
 * - Converts common Ag-Grid columnDefs to MUI columns for easier drop-in usage
 *
 * Props (compat with NexusGrid):
 * - title?: string
 * - actionText?: string
 * - onAdd?: () => void
 * - columnDefs?: any[] (AG-like or MUI columns)
 * - rowData?: any[]
 * - defaultColDef?: object (partial support; maps filter->filterable, sortable, width, flex)
 * - gridRef?: React.RefObject
 * - onOpenSql?: () => void
 * - onOpenMapping?: () => void
 * - onSqlApply?: (query: string) => void
 * - sqlInitialQuery?: string
 * - sqlLanguage?: string
 * - sqlEditorTitle?: string
 * - showRowNumbers?: boolean
 * - showActions?: boolean
 * - className?: string (applied to inner wrapper)
 * - containerClassName?: string (outer container)
 * - style?: React.CSSProperties (applied to grid container for sizing)
 * - children?: React.ReactNode (if provided, bypasses grid rendering)
 * - ...gridProps: passed through to DataGridPremium
 */
const NexusDataGrid = ({
  title,
  actionText,
  onAdd,
  columnDefs = [],
  rowData = [],
  defaultColDef,
  gridRef: externalGridRef,
  onOpenSql,
  onOpenMapping,
  onSqlApply,
  sqlInitialQuery = "",
  sqlLanguage = "sql",
  sqlEditorTitle,
  showRowNumbers = false,
  showActions = true,
  className = "",
  containerClassName = "border border-slate-200 rounded-lg bg-white shadow-sm mb-6",
  style = { height: 480, width: "100%" },
  children,
  ...gridProps
}) => {
  const internalRef = useRef(null);
  const gridRef = externalGridRef || internalRef;
  const apiRef = useGridApiRef();

  const [isSqlOpen, setIsSqlOpen] = useState(false);
  const [sqlValue, setSqlValue] = useState("");
  const [columnWidths, setColumnWidths] = useState({});

  // Memoize context to avoid changing identity across renders
  const gridContext = useMemo(
    () => gridProps?.context ?? {},
    [gridProps.context]
  );

  // Keep a map of per-field editable functions (from AG-style defs)
  const editableFnsRef = useRef({});

  const muiColumns = useMemo(() => {
    let finalColumns = [];

    // Add row number column if enabled
    if (showRowNumbers) {
      finalColumns.push({
        field: "__rowNum",
        headerName: "#",
        width: 60,
        sortable: false,
        filterable: false,
        editable: false,
        type: "number",
        renderCell: (params) => params.api.getRowIndex(params.id) + 1,
      });
    }

    const toMuiCol = (c) => {
      if (!c) return null;
      // Build a MUI column model (even for MUI-style defs so we can augment)
      const originalCol = c;
      const mc = {
        ...(typeof c === "object" ? c : {}),
        field:
          c.field ||
          c.colId ||
          c.headerName ||
          Math.random().toString(36).slice(2),
        headerName: c.headerName || c.field,
        // Use fixed width to allow user resizing to persist; derive from flex if no explicit width
        width:
          typeof c.width === "number"
            ? c.width
            : typeof c.flex === "number"
            ? Math.max(120, Math.round(160 * c.flex))
            : undefined,
        sortable: typeof c.sortable === "boolean" ? c.sortable : true,
        filterable: typeof c.filter === "boolean" ? c.filter : true,
        editable: typeof c.editable === "function" ? true : !!c.editable,
        // Map AG's valueFormatter to MUI's valueFormatter signature
        valueFormatter: c.valueFormatter
          ? (params) =>
              c.valueFormatter({
                value: params.value,
                data: params.row,
                colDef: c,
              })
          : undefined,
        // Basic valueGetter passthrough if provided
        valueGetter: c.valueGetter
          ? (params) => c.valueGetter({ data: params.row, colDef: c })
          : undefined,
      };
      // Apply persisted user width if available
      if (mc.field && typeof columnWidths[mc.field] === "number") {
        mc.width = columnWidths[mc.field];
      }
      // IMPORTANT: remove flex so user-resized widths stick (flex columns recalc width)
      if (typeof mc.flex !== "undefined") {
        delete mc.flex;
      }
      // Track per-field editable functions to emulate AG's dynamic editable
      if (typeof c.editable === "function" && mc.field) {
        editableFnsRef.current[mc.field] = (rowParams) =>
          c.editable({ data: rowParams.row, colDef: originalCol });
      }
      // Adapter for cellRenderer (React component) -> renderCell
      if (c.cellRenderer) {
        const Renderer = c.cellRenderer;
        mc.renderCell = (params) => {
          // Build an AG-like props object for existing renderers
          const agLike = {
            value: params.value,
            data: params.row,
            colDef: c,
            context: gridContext,
            api: params.api,
            id: params.id,
            field: params.field,
          };
          return <Renderer {...agLike} />;
        };
      }

      // Wrap user-provided renderCell to inject data/context for legacy renderers
      if (typeof c.renderCell === "function") {
        const UserRC = c.renderCell;
        mc.renderCell = (params) => (
          <UserRC
            {...params}
            value={params.value}
            data={params.row}
            context={gridContext}
            colDef={c}
          />
        );
      }

      // If caller provided a native MUI renderEditCell, honor it
      if (typeof c.renderEditCell === "function") {
        mc.renderEditCell = c.renderEditCell;
      }

      // Carry over custom provider used by some editors (e.g., MergeValueEditCell)
      if (typeof c.getMergeOptions === "function") {
        mc.getMergeOptions = c.getMergeOptions;
      }

      // Skip row number column from column processing
      if (c.field === "__rowNum") {
        return c;
      }

      // Editor mappings
      // 1) AG select editor -> MUI singleSelect
      if (
        (c.cellEditor === "agSelectCellEditor" ||
          c.cellEditor === "agSelectCellEditor") &&
        Array.isArray(c.cellEditorParams?.values)
      ) {
        mc.type = "singleSelect";
        mc.valueOptions = c.cellEditorParams.values;
      }
      // 2) Multi-select editor heuristic -> MUI Autocomplete multiple
      if (Array.isArray(c.cellEditorParams?.options)) {
        mc.valueOptions = c.cellEditorParams.options;
        mc.renderEditCell = (params) => (
          <MultiSelectCheckEditCell {...params} />
        );
      }
      // 0) Custom React editor component from AG-style colDef (e.g., GooglePlacesEditor)
      // Note: placed AFTER the selectors above so multi-select/select cases take precedence.
      if (typeof c.cellEditor === "function" && !mc.renderEditCell) {
        const EditorComp = c.cellEditor;
        mc.renderEditCell = (params) => {
          const stopEditing = (cancel) =>
            params.api.stopCellEditMode({
              id: params.id,
              field: params.field,
              ignoreModifications: !!cancel,
            });
          const onValueChange = (v) =>
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: v,
            });
          // Provide AG-like props to the editor for compatibility
          const agLikeEditorProps = {
            value: params.value,
            onValueChange,
            stopEditing,
            eventKey: undefined,
            data: params.row,
            colDef: c,
            context: gridContext,
          };
          return <EditorComp {...agLikeEditorProps} />;
        };
      }
      // If caller provided native renderEditCell, wrap to also pass context/data for compatibility
      if (typeof c.renderEditCell === "function") {
        const UserREC = c.renderEditCell;
        mc.renderEditCell = (params) => (
          <UserREC
            {...params}
            value={params.value}
            data={params.row}
            context={gridContext}
            colDef={c}
          />
        );
      }
      // Carry over custom provider used by some editors (e.g., MergeValueEditCell)
      if (typeof c.getMergeOptions === "function") {
        mc.getMergeOptions = c.getMergeOptions;
      }

      // 3) Fallback for custom editors: basic text field
      // Only apply when no native/editor type is defined (avoid overriding singleSelect, etc.)
      if (!mc.renderEditCell && !mc.type && c.editable) {
        mc.renderEditCell = (params) => (
          <TextField
            size="small"
            value={params.value ?? ""}
            onChange={(e) =>
              params.api.setEditCellValue(
                { id: params.id, field: params.field, value: e.target.value },
                e
              )
            }
            fullWidth
            autoFocus
          />
        );
      }
      return mc;
    };

    const base = Array.isArray(columnDefs)
      ? columnDefs.map(toMuiCol).filter(Boolean)
      : [];

    // Prepend Row # column
    if (showRowNumbers) {
      const hasRowNum = base.some((c) => c.field === "__rowNum");
      if (!hasRowNum) {
        base.unshift({
          field: "__rowNum",
          headerName: "#",
          width: 70,
          sortable: false,
          filterable: false,
          align: "center",
          headerAlign: "center",
          renderCell: (params) => {
            // MUI DataGrid uses getRowIndexRelativeToVisibleRows
            const index = params.api.getRowIndexRelativeToVisibleRows(
              params.id
            );
            return typeof index === "number" ? index + 1 : "";
          },
        });
      }
    }

    // Append Actions column
    if (showActions) {
      const hasActions = base.some((c) => c.field === "actions");
      if (!hasActions) {
        base.push({
          field: "actions",
          headerName: "Actions",
          width: 100,
          sortable: false,
          filterable: false,
          align: "center",
          headerAlign: "center",
          renderCell: (params) => (
            <ActionsRenderer data={params.row} context={gridContext} />
          ),
        });
      }
    }

    return base;
  }, [columnDefs, showRowNumbers, showActions, gridContext, columnWidths]);

  const displayedActionText =
    actionText && actionText.trim() ? actionText : "Add New Row";

  const mergedProcessRowUpdate = useCallback(
    (newRow, oldRow) => {
      // Let consumer handle row update if provided
      if (typeof gridProps.processRowUpdate === "function") {
        try {
          return gridProps.processRowUpdate(newRow, oldRow);
        } catch (_) {}
      }
      return newRow;
    },
    [gridProps]
  );

  // Open SQL Editor
  const handleOnOpenSql = () => {
    setSqlValue(sqlInitialQuery || "");
    setIsSqlOpen(true);
    if (onOpenSql) {
      try {
        onOpenSql();
      } catch (_) {}
    }
  };

  const handleOnSqlApply = (q) => {
    setSqlValue(q ?? "");
    if (onSqlApply) {
      try {
        onSqlApply(q);
      } catch (_) {}
    }
    setIsSqlOpen(false);
  };

  // Persist user width changes to avoid resets when columns re-render
  const handleColumnResize = useCallback((params) => {
    try {
      const f = params?.colDef?.field ?? params?.field;
      const w = Math.round(params?.width || 0);
      if (f && w > 0) setColumnWidths((prev) => ({ ...prev, [f]: w }));
    } catch (_) {}
  }, []);

  const handleColumnWidthChange = useCallback((params) => {
    try {
      const f = params?.colDef?.field ?? params?.field;
      const w = Math.round(params?.width || 0);
      if (f && w > 0) setColumnWidths((prev) => ({ ...prev, [f]: w }));
    } catch (_) {}
  }, []);

  // Single-click to edit: start edit on cell click if editable
  const handleCellClick = useCallback(
    (params, event) => {
      try {
        // Avoid entering edit if user clicked on actions column or non-editable
        const col = muiColumns.find((c) => c.field === params.field);
        if (!col) return;
        // Respect isCellEditable if provided
        const editable =
          typeof gridProps.isCellEditable === "function"
            ? !!gridProps.isCellEditable(params)
            : !!col.editable;
        if (!editable) return;
        // Prevent default selection behavior
        if (event && typeof event.defaultMuiPrevented !== "undefined") {
          event.defaultMuiPrevented = true;
        }
        apiRef.current.startCellEditMode({
          id: params.id,
          field: params.field,
        });
      } catch (_) {}
    },
    [muiColumns, gridProps, apiRef]
  );

  return (
    <div className={containerClassName}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <div className="text-base font-medium text-slate-800">
          {title || ""}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="px-3 py-2 rounded bg-blue-600 text-white text-sm hover:bg-blue-700"
            onClick={onAdd}
          >
            {displayedActionText}
          </button>
          <button
            type="button"
            title="Open SQL editor"
            aria-label="Open SQL editor"
            className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-purple-300 bg-purple-50 text-purple-600 hover:bg-purple-100 hover:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-300"
            onClick={handleOnOpenSql}
          >
            <Database size={18} />
          </button>
          <button
            type="button"
            title="Open mapping"
            aria-label="Open mapping"
            className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 hover:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-300"
            onClick={onOpenMapping}
          >
            <Map size={18} />
          </button>
        </div>
      </div>
      <div className="p-4">
        {children ? (
          children
        ) : (
          <div
            className={`${className} rounded-lg`}
            style={style}
            ref={gridRef}
          >
            <DataGridPremium
              apiRef={apiRef}
              rows={Array.isArray(rowData) ? rowData : []}
              columns={muiColumns}
              disableRowSelectionOnClick
              pageSizeOptions={[10, 25, 50, 100]}
              pagination
              initialState={{
                pagination: { paginationModel: { pageSize: 25, page: 0 } },
              }}
              getRowId={gridProps.getRowId}
              isCellEditable={(params) => {
                const fn = editableFnsRef.current?.[params.field];
                if (typeof fn === "function") return !!fn(params);
                const col = muiColumns.find((c) => c.field === params.field);
                return !!col?.editable;
              }}
              processRowUpdate={mergedProcessRowUpdate}
              onProcessRowUpdateError={(e) => console.error(e)}
              getRowClassName={gridProps.getRowClassName}
              onColumnResize={handleColumnResize}
              onColumnWidthChange={handleColumnWidthChange}
              onCellClick={handleCellClick}
              {...gridProps}
            />
          </div>
        )}
      </div>
      <NexusQueryEditor
        isOpen={isSqlOpen}
        initialQuery={sqlValue}
        language={sqlLanguage}
        title={sqlEditorTitle || `${title || ""} SQL Query Editor`}
        onApply={handleOnSqlApply}
        onCancel={() => setIsSqlOpen(false)}
      />
    </div>
  );
};

export default NexusDataGrid;
